
import { Member, MemberPermission } from "../../types/memberTypes";

/**
 * Generate initials from first and last name
 */
export const generateAvatarInitials = (firstName: string, lastName: string): string => {
  const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
  return `${firstInitial}${lastInitial}`;
};

/**
 * Get checked permission count for a member
 */
export const countCheckedPermissions = (permissions?: MemberPermission[]): number => {
  if (!permissions) return 0;
  return permissions.filter(p => p.checked).length;
};

/**
 * Get list of checked permission names for a member
 */
export const getCheckedPermissionNames = (permissions?: MemberPermission[]): string[] => {
  if (!permissions) return [];
  return permissions
    .filter(p => p.checked)
    .map(p => p.name);
};

/**
 * Find the current user in a list of members
 */
export const findCurrentUser = (members: Member[]): Member | undefined => {
  return members.find(member => member.isCurrentUser);
};

/**
 * Check if the current user has a specific role
 */
export const hasRole = (members: Member[], role: string): boolean => {
  const currentUser = findCurrentUser(members);
  return !!currentUser && currentUser.role === role;
};

/**
 * Check if a member is the primary owner
 */
export const isPrimaryOwner = (member: Member): boolean => {
  return member.role === 'Primary Owner';
};

/**
 * Check if a member is any type of owner (Primary or regular)
 */
export const isOwner = (member: Member): boolean => {
  return member.role === 'Primary Owner' || member.role === 'Owner';
};

/**
 * Check if a member has a specific permission
 */
export const hasPermission = (member: Member, permissionId: string): boolean => {
  // Owners always have all permissions
  if (isOwner(member)) return true;
  
  // Otherwise check specific permission
  return !!member.permissions?.some(p => p.id === permissionId && p.checked);
};
